import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Building2, Plus, X, Loader2, ArrowRightLeft, Link2 } from 'lucide-react'
import toast from 'react-hot-toast'
import { api, formatMoney } from '@/lib/api'

interface Parceira {
  pessoa_id: number
  pessoa_codigo: string | null
  empresa_id: number
  razao_social: string
  nome_fantasia: string
  cnpj: string | null
}

interface Produto { id: number; codigo: string; descricao: string; preco_custo: number; ultimo_custo: number }

export default function IntercompanyPage() {
  const nav = useNavigate()
  const qc = useQueryClient()
  const [parceira, setParceira] = useState<Parceira | null>(null)

  const { data: parcs, isLoading } = useQuery<{ parceiras: Parceira[] }>({
    queryKey: ['intercompany-parceiras'],
    queryFn: async () => (await api.get('/intercompany/parceiras')).data,
  })

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ArrowRightLeft className="text-brand-600" /> Compras Intercompany
          </h1>
          <p className="text-ink-600 text-sm">
            Pedido de compra de empresas do mesmo grupo. Gera automaticamente um pedido de venda na empresa fornecedora.
          </p>
        </div>
        <button onClick={() => nav('/compras')} className="btn-secondary text-sm">← Compras</button>
      </div>

      {isLoading && <div className="p-8 text-center">Carregando...</div>}

      {!isLoading && (!parcs?.parceiras || parcs.parceiras.length === 0) && (
        <div className="card card-body text-center py-12">
          <Link2 className="mx-auto text-ink-400 mb-3" size={40} />
          <div className="text-lg font-semibold">Nenhuma empresa do grupo ligada</div>
          <div className="text-sm text-ink-600 mt-1">
            Um super admin precisa ligar as empresas primeiro. Veja em SuperAdmin → Grupos.
          </div>
        </div>
      )}

      {parcs?.parceiras && parcs.parceiras.length > 0 && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {parcs.parceiras.map(p => (
            <div key={p.pessoa_id} className="card card-body hover:border-brand-400 cursor-pointer"
              onClick={() => setParceira(p)}>
              <div className="flex items-start gap-3">
                <Building2 className="text-brand-600 shrink-0" size={24} />
                <div className="flex-1">
                  <div className="font-semibold">{p.nome_fantasia}</div>
                  <div className="text-xs text-ink-600">{p.razao_social}</div>
                  {p.cnpj && <div className="text-xs font-mono text-ink-500 mt-1">{p.cnpj}</div>}
                </div>
              </div>
              <button className="btn-primary w-full mt-3 text-sm">
                <Plus size={14} /> Novo pedido
              </button>
            </div>
          ))}
        </div>
      )}

      {parceira && (
        <NovoPedidoIntercompanyModal parceira={parceira}
          onClose={() => setParceira(null)}
          onDone={() => {
            setParceira(null)
            qc.invalidateQueries({ queryKey: ['compras-pedidos'] })
            toast.success('Pedido enviado — a empresa parceira já recebeu como pedido de venda')
            nav('/compras')
          }} />
      )}
    </div>
  )
}

function NovoPedidoIntercompanyModal({
  parceira, onClose, onDone,
}: { parceira: Parceira; onClose: () => void; onDone: () => void }) {
  const [observacoes, setObservacoes] = useState('')
  const [condicao, setCondicao] = useState('30 dias')
  const [itens, setItens] = useState<Array<{ produto_id: number | ''; quantidade: string; preco_unitario: string }>>([
    { produto_id: '', quantidade: '1', preco_unitario: '0' },
  ])

  const { data: produtos = [] } = useQuery<Produto[]>({
    queryKey: ['produtos-ic'],
    queryFn: async () => (await api.get('/cadastros/produtos', { params: { limit: 500 } })).data,
  })

  const total = itens.reduce((s, i) =>
    s + (parseFloat(i.quantidade) || 0) * (parseFloat(i.preco_unitario) || 0), 0)

  const pronto = itens.length > 0 && itens.every(i => !!i.produto_id && parseFloat(i.quantidade) > 0)

  const criar = useMutation({
    mutationFn: async () => (await api.post('/compras/pedidos', {
      fornecedor_id: parceira.pessoa_id,
      condicao_pagamento: condicao,
      observacoes: observacoes || null,
      itens: itens.map(i => ({
        produto_id: i.produto_id,
        quantidade: parseFloat(i.quantidade),
        preco_unitario: parseFloat(i.preco_unitario),
        valor_desconto: 0,
      })),
    })).data,
    onSuccess: async (d: any) => {
      // Automaticamente confirma pra disparar o handler intercompany
      try {
        await api.post(`/compras/pedidos/${d.id}/confirmar`)
        onDone()
      } catch {
        toast.error('Pedido criado mas não confirmado — confirme manualmente')
        onDone()
      }
    },
  })

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[95vh] overflow-auto">
        <div className="flex items-center justify-between border-b px-5 py-3">
          <h3 className="font-semibold">Comprar de {parceira.nome_fantasia}</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="bg-sky-50 border border-sky-200 p-2 rounded text-xs text-sky-900">
            Os produtos precisam existir com o mesmo código em ambas as empresas.
            Ao confirmar, a <strong>{parceira.nome_fantasia}</strong> recebe automaticamente um pedido de venda,
            que entra no fluxo normal (separação, produção se industrial, expedição).
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <div className="text-xs font-medium mb-1">Condição de pagamento</div>
              <input className="input w-full" value={condicao} onChange={e => setCondicao(e.target.value)} />
            </label>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="text-sm font-semibold">Itens</div>
              <button onClick={() => setItens([...itens, { produto_id: '', quantidade: '1', preco_unitario: '0' }])}
                className="btn-secondary !py-1 !px-2 text-xs"><Plus size={12} /> Item</button>
            </div>
            {itens.map((it, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 items-end p-2 border rounded bg-ink-50 mb-2">
                <div className="col-span-7">
                  <div className="text-[10px] uppercase text-ink-500 mb-0.5">Produto</div>
                  <select className="input w-full text-sm" value={it.produto_id}
                    onChange={e => {
                      const id = Number(e.target.value)
                      const p = produtos.find(pp => pp.id === id)
                      const preco = Number(p?.ultimo_custo || p?.preco_custo || 0)
                      setItens(itens.map((t, i) => i === idx ? { ...t, produto_id: id, preco_unitario: String(preco) } : t))
                    }}>
                    <option value="">—</option>
                    {produtos.map(p => <option key={p.id} value={p.id}>{p.codigo} - {p.descricao}</option>)}
                  </select>
                </div>
                <div className="col-span-2">
                  <div className="text-[10px] uppercase text-ink-500 mb-0.5">Qtd</div>
                  <input type="number" step="0.001" className="input w-full text-sm" value={it.quantidade}
                    onChange={e => setItens(itens.map((t, i) => i === idx ? { ...t, quantidade: e.target.value } : t))} />
                </div>
                <div className="col-span-2">
                  <div className="text-[10px] uppercase text-ink-500 mb-0.5">R$ unit.</div>
                  <input type="number" step="0.0001" className="input w-full text-sm" value={it.preco_unitario}
                    onChange={e => setItens(itens.map((t, i) => i === idx ? { ...t, preco_unitario: e.target.value } : t))} />
                </div>
                <div className="col-span-1 text-right">
                  {itens.length > 1 && (
                    <button onClick={() => setItens(itens.filter((_, i) => i !== idx))}
                      className="text-rose-600 p-1"><X size={14} /></button>
                  )}
                </div>
              </div>
            ))}
            <div className="flex justify-between mt-2 text-sm">
              <span className="text-ink-600">Total:</span>
              <strong>{formatMoney(total)}</strong>
            </div>
          </div>

          <label className="block">
            <div className="text-xs font-medium mb-1">Observações</div>
            <textarea className="input w-full" rows={2} value={observacoes} onChange={e => setObservacoes(e.target.value)} />
          </label>
        </div>
        <div className="border-t px-5 py-3 flex justify-end gap-2 bg-ink-50">
          <button onClick={onClose} className="btn-secondary">Cancelar</button>
          <button disabled={!pronto || criar.isPending} onClick={() => criar.mutate()} className="btn-primary">
            {criar.isPending && <Loader2 size={14} className="animate-spin" />}
            Enviar pedido
          </button>
        </div>
      </div>
    </div>
  )
}
